import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Save, Sparkles, ImageIcon, Wand2, Upload, Plus, Trash2, Star, Video, AlertTriangle, Download, Share2 } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import I18nTripleFields, { I18nTriple } from '../../components/Admin/I18nTripleFields';
import { emptyI18nTriple } from '../../i18n/localizeContent';
import IconPicker from '../../components/Admin/IconPicker';
import { resolveIcon } from '../../utils/iconCatalog';
import { focalObjectPosition } from '../../utils/focalPoint';
import LandingQuizEditor, { LandingQuiz, emptyLandingQuiz, hydrateLandingQuiz } from '../../components/Admin/LandingQuizEditor';
import CtaTargetPicker, { CtaTarget, emptyCtaTarget } from '../../components/Admin/CtaTargetPicker';
import PriceDisplayPicker, { PriceDisplay, emptyPriceDisplay } from '../../components/Admin/PriceDisplayPicker';
import HeroVideoSlots, { HeroVideos } from '../../components/Admin/HeroVideoSlots';
import { Button, Checkbox, IconButton, Tabs } from '../../components/UI';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

interface PixabayOption {
  pixabayId: number;
  previewURL: string;
  largeImageURL: string;
  tags: string;
}

type SocialCardFormat = 'story' | 'portrait' | 'square' | 'og' | 'whatsapp';
type CardLang = 'pt' | 'en' | 'es';

interface SocialCardItem {
  format: SocialCardFormat;
  lang: CardLang;
  url: string;
  width: number;
  height: number;
}

const SOCIAL_CARD_FORMATS: { format: SocialCardFormat; label: string; hint: string; aspect: string; postable: boolean }[] = [
  { format: 'story', label: '9:16', hint: 'Reels / Stories', aspect: 'aspect-[9/16]', postable: false },
  { format: 'portrait', label: '4:5', hint: 'Feed', aspect: 'aspect-[4/5]', postable: true },
  { format: 'square', label: '1:1', hint: 'Feed', aspect: 'aspect-square', postable: true },
  { format: 'og', label: '1.91:1', hint: 'Preview de link', aspect: 'aspect-[1200/630]', postable: false },
  { format: 'whatsapp', label: 'Foto do grupo', hint: 'WhatsApp', aspect: 'aspect-square', postable: false }
];
const CARD_LANGS: CardLang[] = ['pt', 'en', 'es'];

interface AvatarOption {
  _id: string;
  name: string;
  slug: string;
}

interface TeamMemberOption {
  _id: string;
  nome: string;
  cargo?: string;
}

interface Benefit {
  _id?: string;
  icon: string;
  i18n: { pt: { text: string }; en: { text: string }; es: { text: string } };
}

interface SocialProofItem {
  _id?: string;
  value: string;
  i18n: { pt: { label: string }; en: { label: string }; es: { label: string } };
}

interface TestimonialOption {
  _id: string;
  author: string;
  country?: string;
  text?: string;
  rating?: number;
  status: 'pending' | 'approved' | 'rejected';
  consentMedia?: boolean;
  video?: { url?: string };
}

const MAX_TESTIMONIALS = 3;
// Mesmo limite e formatos aceitos por POST /landing-pages/upload-hero-image (SVG bloqueado).
const HERO_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const HERO_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
// Prévias do recorte (mesma semântica de background-position das peças em landingSocialCardService).
const HERO_CROP_PREVIEWS = [
  { label: '9:16', w: 9, h: 16, width: 54 },
  { label: '4:5', w: 4, h: 5, width: 77 },
  { label: '1:1', w: 1, h: 1, width: 96 },
  { label: '1.91:1', w: 504, h: 630, width: 77 },
  { label: 'Página', w: 16, h: 7, width: 220 }
];
const HERO_IMAGE_SOURCE_LABELS: Record<string, string> = { upload: 'Upload', link: 'Link', pixabay: 'Pixabay', ai: 'IA' };

interface LandingPageFormData {
  internalName: string;
  slug: string;
  avatar: string;
  teamMember: string;
  ctaTarget: CtaTarget;
  priceDisplay: PriceDisplay;
  paymentTrust: { enabled: boolean };
  status: 'draft' | 'published';
  hero: {
    mediaType: 'image' | 'video';
    image: { source: 'upload' | 'pixabay' | 'ai' | 'link'; url: string; focusX?: number; focusY?: number };
    video: { source: 'upload' | 'link'; url: string; autoplayMuted?: boolean };
  };
}

const emptyBenefit = (): Benefit => ({ icon: '', i18n: { pt: { text: '' }, en: { text: '' }, es: { text: '' } } });
const emptySocialProofItem = (): SocialProofItem => ({ value: '', i18n: { pt: { label: '' }, en: { label: '' }, es: { label: '' } } });

const LandingPageForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isEditing = Boolean(id);
  const notifications = useNotifications();
  const { copy } = useCopyToClipboard();
  const heroImageInputRef = useRef<HTMLInputElement>(null);

  const [i18n, setI18n] = useState<I18nTriple>(emptyI18nTriple());
  const [subject, setSubject] = useState('');
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [socialProof, setSocialProof] = useState<SocialProofItem[]>([]);
  const [testimonialIds, setTestimonialIds] = useState<string[]>([]);
  const [allTestimonials, setAllTestimonials] = useState<TestimonialOption[]>([]);
  const [quiz, setQuiz] = useState<LandingQuiz>(emptyLandingQuiz());
  const [avatars, setAvatars] = useState<AvatarOption[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMemberOption[]>([]);
  const [formData, setFormData] = useState<LandingPageFormData>({
    internalName: '',
    slug: '',
    avatar: '',
    teamMember: '',
    ctaTarget: emptyCtaTarget(),
    priceDisplay: emptyPriceDisplay(),
    paymentTrust: { enabled: false },
    status: 'draft',
    hero: { mediaType: 'image', image: { source: 'upload', url: '' }, video: { source: 'link', url: '' } }
  });

  const [loading, setLoading] = useState(false);
  const [generatingText, setGeneratingText] = useState(false);
  const [searchingImages, setSearchingImages] = useState(false);
  const [generatingAiImage, setGeneratingAiImage] = useState(false);
  const [heroVideos, setHeroVideos] = useState<HeroVideos>({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [heroImageSize, setHeroImageSize] = useState<{ w: number; h: number } | null>(null);
  const [pixabayOptions, setPixabayOptions] = useState<PixabayOption[]>([]);
  const [aiPromptInput, setAiPromptInput] = useState('');
  const [showAiImagePrompt, setShowAiImagePrompt] = useState(false);
  const [openIconPickerIndex, setOpenIconPickerIndex] = useState<number | null>(null);
  const [socialCards, setSocialCards] = useState<SocialCardItem[]>([]);
  const [socialCardsStale, setSocialCardsStale] = useState(false);
  const [cardLangs, setCardLangs] = useState<CardLang[]>(['pt']);
  const [previewLang, setPreviewLang] = useState<CardLang>('pt');
  const [generatingCards, setGeneratingCards] = useState(false);
  const [creatingPostFormat, setCreatingPostFormat] = useState<SocialCardFormat | null>(null);

  useEffect(() => {
    axios.get('/avatars?limit=100').then((res) => setAvatars(res.data.avatars)).catch(() => setAvatars([]));
  }, []);

  useEffect(() => {
    axios.get('/testimonials/all').then((res) => setAllTestimonials(res.data)).catch(() => setAllTestimonials([]));
  }, []);

  useEffect(() => {
    axios.get('/equipe/admin').then((res) => setTeamMembers(res.data)).catch(() => setTeamMembers([]));
  }, []);

  const fetchLandingPage = useCallback(async () => {
    if (!id) return;
    try {
      const response = await axios.get(`/landing-pages/${id}`);
      const lp = response.data;
      setFormData({
        internalName: lp.internalName || '',
        slug: lp.slug || '',
        avatar: lp.avatar?._id || lp.avatar || '',
        teamMember: lp.teamMember?._id || lp.teamMember || '',
        ctaTarget: { kind: lp.ctaTarget?.kind || 'avatar', value: lp.ctaTarget?.value || '' },
        priceDisplay: {
          enabled: !!lp.priceDisplay?.enabled,
          communitySlug: lp.priceDisplay?.communitySlug || '',
          currency: lp.priceDisplay?.currency || 'both'
        },
        paymentTrust: { enabled: !!lp.paymentTrust?.enabled },
        status: lp.status || 'draft',
        hero: lp.hero
          ? { ...lp.hero, video: { ...lp.hero.video, autoplayMuted: lp.hero.video?.autoplayMuted !== false } }
          : { mediaType: 'image', image: { source: 'upload', url: '' }, video: { source: 'link', url: '', autoplayMuted: true } }
      });
      setBenefits(lp.benefits || []);
      setSocialProof(lp.socialProof || []);
      setTestimonialIds((lp.testimonials || []).map((t: any) => String(t?._id || t)));
      setQuiz(hydrateLandingQuiz(lp.quiz));
      setSocialCards(lp.socialCards?.items || []);
      setHeroVideos(lp.heroVideos || {});
      setSocialCardsStale(Boolean(lp.socialCardsStale));
      setI18n({
        pt: { ...lp.i18n?.pt },
        en: { ...lp.i18n?.en },
        es: { ...lp.i18n?.es }
      });
    } catch (error) {
      console.error('Erro ao carregar landing page:', error);
    }
  }, [id]);

  useEffect(() => {
    if (isEditing) fetchLandingPage();
  }, [isEditing, fetchLandingPage]);

  const handleGenerateContent = async () => {
    if (!subject.trim()) {
      notifications.showWarning('Validação', 'Informe o assunto/oferta antes de gerar o conteúdo.');
      return;
    }
    try {
      setGeneratingText(true);
      const response = await axios.post('/landing-pages/generate-content', { subject });
      const { headline, subheadline, ctaLabel, benefits: genBenefits, metaTitle, metaDescription, imageQuery } = response.data;
      setI18n((prev) => ({
        pt: { ...prev.pt, headline: headline?.pt, subheadline: subheadline?.pt, ctaLabel: ctaLabel?.pt, metaTitle: metaTitle?.pt, metaDescription: metaDescription?.pt },
        en: { ...prev.en, headline: headline?.en, subheadline: subheadline?.en, ctaLabel: ctaLabel?.en, metaTitle: metaTitle?.en, metaDescription: metaDescription?.en },
        es: { ...prev.es, headline: headline?.es, subheadline: subheadline?.es, ctaLabel: ctaLabel?.es, metaTitle: metaTitle?.es, metaDescription: metaDescription?.es }
      }));
      if (Array.isArray(genBenefits) && genBenefits.length > 0) {
        setBenefits(genBenefits.map((b: any) => ({
          icon: '',
          i18n: { pt: { text: b.pt || '' }, en: { text: b.en || '' }, es: { text: b.es || '' } }
        })));
      }
      setPixabayOptions([]);
      setAiPromptInput(imageQuery || subject);
      if (!formData.internalName) {
        setFormData((prev) => ({ ...prev, internalName: subject }));
      }
    } catch (error: any) {
      console.error('Erro ao gerar conteúdo:', error);
      notifications.showError('Erro ao gerar conteúdo', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setGeneratingText(false);
    }
  };

  const handleSearchPixabay = async () => {
    const query = aiPromptInput || subject;
    if (!query.trim()) {
      notifications.showWarning('Validação', 'Informe o assunto ou gere o conteúdo antes de buscar imagens.');
      return;
    }
    try {
      setSearchingImages(true);
      const response = await axios.post('/landing-pages/fetch-image-options', { subject, query });
      setPixabayOptions(response.data.options || []);
    } catch (error: any) {
      notifications.showError('Erro ao buscar imagens', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setSearchingImages(false);
    }
  };

  const handleSelectPixabayImage = async (option: PixabayOption) => {
    try {
      setSearchingImages(true);
      const response = await axios.post('/landing-pages/select-image', { pixabayId: option.pixabayId, imageURL: option.largeImageURL });
      setHeroImage({ source: 'pixabay', url: response.data.url });
      setPixabayOptions([]);
    } catch (error: any) {
      notifications.showError('Erro ao salvar imagem', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setSearchingImages(false);
    }
  };

  const handleGenerateAiImage = async () => {
    if (!aiPromptInput.trim()) {
      notifications.showWarning('Validação', 'Descreva a imagem que deseja gerar.');
      return;
    }
    try {
      setGeneratingAiImage(true);
      const response = await axios.post('/landing-pages/generate-ai-image', { prompt: aiPromptInput });
      setHeroImage({ source: 'ai', url: response.data.url });
      setShowAiImagePrompt(false);
    } catch (error: any) {
      notifications.showError('Erro ao gerar imagem', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setGeneratingAiImage(false);
    }
  };

  const handleGenerateSocialCards = async () => {
    if (!id || cardLangs.length === 0) return;
    setGeneratingCards(true);
    try {
      const response = await axios.post(`/landing-pages/${id}/social-cards`, { langs: cardLangs });
      setSocialCards(response.data.socialCards?.items || []);
      setSocialCardsStale(false);
      if (!cardLangs.includes(previewLang)) setPreviewLang(cardLangs[0]);
      notifications.showSuccess('Peças geradas', 'As peças para redes sociais estão prontas.');
    } catch (error: any) {
      notifications.showError('Erro', error.response?.data?.message || 'Não foi possível gerar as peças');
    } finally {
      setGeneratingCards(false);
    }
  };

  const handleCreateSocialPost = async (format: SocialCardFormat) => {
    if (!id) return;
    setCreatingPostFormat(format);
    try {
      const response = await axios.post(`/landing-pages/${id}/social-post`, { format, lang: previewLang });
      navigate(`/admin/redes-sociais/${response.data.id}/edit`);
    } catch (error: any) {
      notifications.showError('Erro', error.response?.data?.message || 'Não foi possível criar o post');
    } finally {
      setCreatingPostFormat(null);
    }
  };

  const toggleCardLang = (lang: CardLang) => {
    setCardLangs((prev) => (prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang]));
  };

  const cardsForPreview = socialCards.filter((card) => card.lang === previewLang);
  const generatedLangs = CARD_LANGS.filter((lang) => socialCards.some((card) => card.lang === lang));

  // Imagem nova volta o foco para o centro.
  const setHeroImage = (image: LandingPageFormData['hero']['image']) =>
    setFormData((prev) => ({ ...prev, hero: { ...prev.hero, mediaType: 'image', image: { focusX: 50, focusY: 50, ...image } } }));

  const setHeroFocus = (focusX: number, focusY: number) =>
    setFormData((prev) => ({ ...prev, hero: { ...prev.hero, image: { ...prev.hero.image, focusX, focusY } } }));

  const heroFocusX = formData.hero.image.focusX ?? 50;
  const heroFocusY = formData.hero.image.focusY ?? 50;

  const handleHeroFocusClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (v: number) => Math.min(100, Math.max(0, Math.round(v * 100)));
    setHeroFocus(pct((e.clientX - rect.left) / rect.width), pct((e.clientY - rect.top) / rect.height));
  };

  const handleHeroImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!HERO_IMAGE_TYPES.includes(file.type)) {
      notifications.showWarning('Formato inválido', 'Envie uma imagem PNG, JPG, WebP ou GIF.');
      return;
    }
    if (file.size > HERO_IMAGE_MAX_BYTES) {
      notifications.showWarning('Imagem muito grande', 'A imagem deve ter no máximo 8 MB.');
      return;
    }
    try {
      setUploadingImage(true);
      const data = new FormData();
      data.append('image', file);
      const response = await axios.post('/landing-pages/upload-hero-image', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHeroImage({ source: 'upload', url: response.data.url });
    } catch (error: any) {
      notifications.showError('Erro ao enviar imagem', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setUploadingImage(false);
    }
  };

  const addBenefit = () => setBenefits((prev) => [...prev, emptyBenefit()]);
  const removeBenefit = (index: number) => setBenefits((prev) => prev.filter((_, i) => i !== index));
  const patchBenefit = (index: number, lang: 'pt' | 'en' | 'es', text: string) =>
    setBenefits((prev) => prev.map((b, i) => (i === index ? { ...b, i18n: { ...b.i18n, [lang]: { text } } } : b)));
  const patchBenefitIcon = (index: number, iconName: string) => {
    setBenefits((prev) => prev.map((b, i) => (i === index ? { ...b, icon: iconName } : b)));
    setOpenIconPickerIndex(null);
  };

  // Landing é divulgação: só depoimentos aprovados com autorização de redes sociais (concedida pelo cliente).
  const isEligibleTestimonial = (t: TestimonialOption) => t.status === 'approved' && !!t.consentMedia;
  const eligibleTestimonials = allTestimonials.filter(isEligibleTestimonial);
  const testimonialById = new Map(allTestimonials.map((t) => [t._id, t]));
  const toggleTestimonial = (tid: string) =>
    setTestimonialIds((prev) => {
      if (prev.includes(tid)) return prev.filter((x) => x !== tid);
      return prev.length >= MAX_TESTIMONIALS ? prev : [...prev, tid];
    });
  const moveTestimonial = (index: number, delta: number) =>
    setTestimonialIds((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const addSocialProofItem = () => setSocialProof((prev) => [...prev, emptySocialProofItem()]);
  const removeSocialProofItem = (index: number) => setSocialProof((prev) => prev.filter((_, i) => i !== index));
  const patchSocialProofValue = (index: number, value: string) =>
    setSocialProof((prev) => prev.map((item, i) => (i === index ? { ...item, value } : item)));
  const patchSocialProofLabel = (index: number, lang: 'pt' | 'en' | 'es', label: string) =>
    setSocialProof((prev) => prev.map((item, i) => (i === index ? { ...item, i18n: { ...item.i18n, [lang]: { label } } } : item)));

  const handleSubmit = async (e: React.FormEvent, publishNow?: boolean) => {
    e.preventDefault();
    setLoading(true);

    const status = publishNow ? 'published' : formData.status;

    if (formData.internalName.trim().length < 3) {
      notifications.showWarning('Validação', 'Informe um nome interno para a campanha.');
      setLoading(false);
      return;
    }
    if (!formData.avatar) {
      notifications.showWarning('Validação', 'Selecione o avatar da landing page.');
      setLoading(false);
      return;
    }
    if (formData.ctaTarget.kind !== 'avatar' && !formData.ctaTarget.value.trim()) {
      notifications.showWarning('Validação', 'Escolha o destino do botão (CTA).');
      setLoading(false);
      return;
    }
    if (formData.ctaTarget.kind === 'url' && !/^https?:\/\/\S+$/i.test(formData.ctaTarget.value.trim())) {
      notifications.showWarning('Validação', 'A URL do botão (CTA) precisa começar com https://');
      setLoading(false);
      return;
    }
    if (formData.hero.mediaType === 'video' && formData.hero.video.source === 'upload' && isEditing) {
      const hasUpload = ['landscape', 'portrait'].some((f) => {
        const v = heroVideos[f as 'landscape' | 'portrait'];
        return v?.url || v?.status === 'processing';
      });
      if (!hasUpload && !formData.hero.video.url) {
        notifications.showWarning('Validação', 'Envie pelo menos um vídeo (horizontal ou vertical) ou use um link.');
        setLoading(false);
        return;
      }
    }
    if (status === 'published' && !i18n.pt?.headline?.trim()) {
      notifications.showWarning('Validação', 'A headline (pt) é obrigatória para publicar.');
      setLoading(false);
      return;
    }

    if (quiz.enabled) {
      if (quiz.questions.length === 0) {
        notifications.showWarning('Validação', 'O quiz está ativo, mas não tem perguntas.');
        setLoading(false);
        return;
      }
      if (!quiz.rules.some((r) => r.conditions.length > 0)) {
        notifications.showWarning('Validação', 'O quiz precisa de pelo menos uma regra de qualificação. Sem regra, todos vão para a nutrição.');
        setLoading(false);
        return;
      }
    }

    const payload = { ...formData, status, slug: formData.slug, i18n, benefits, socialProof, testimonials: testimonialIds, quiz };

    try {
      if (isEditing) {
        await axios.put(`/landing-pages/${id}`, payload);
        notifications.showSuccess('Landing page atualizada!', 'As alterações foram salvas.');
      } else {
        await axios.post('/landing-pages', payload);
        notifications.showSuccess('Landing page criada!', 'A página foi criada com sucesso.');
      }
      setTimeout(() => navigate('/admin/landing-pages'), 1000);
    } catch (error: any) {
      console.error('Erro ao salvar landing page:', error);
      notifications.showError('Erro ao salvar', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const publicUrl = formData.slug ? `${window.location.origin}/l/${formData.slug}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-start sm:items-center gap-3 sm:gap-4">
          <IconButton action="back" onClick={() => navigate('/admin/landing-pages')} />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{isEditing ? t('admin.landingPageEdit') : t('admin.landingPageNew')}</h1>
            <p className="text-gray-600">Página de funil de vendas para tráfego pago (Facebook/Instagram Ads).</p>
          </div>
        </div>

        {publicUrl && (
          <div className="card mb-6 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-gray-700 truncate">{publicUrl}</div>
            <Button variant="outline" size="sm" icon="copyLink" onClick={() => copy(publicUrl)} className="shrink-0">
              Copiar link
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assunto / oferta da campanha</label>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Ex: Visto de trabalho para Portugal, processo rápido"
                      className="field field--lg flex-1"
                    />
                    <Button className="whitespace-nowrap"
                      type="button"
                      onClick={handleGenerateContent}
                      disabled={generatingText}>
                      <Sparkles className="w-4 h-4" />
                      <span>{generatingText ? 'Gerando...' : 'Gerar conteúdo com IA'}</span>
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome interno (campanha)</label>
                    <input
                      type="text"
                      value={formData.internalName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, internalName: e.target.value }))}
                      className="field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL curta do anúncio)</label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
                      placeholder="visto-trabalho-natal"
                      className="field"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <I18nTripleFields mode="landingPage" value={i18n} onChange={setI18n} />
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Benefícios (bullets)</h3>
                  <Button variant="outline" size="sm" className="shrink-0" type="button" onClick={addBenefit}>
                    <Plus className="w-4 h-4" /> Adicionar
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mb-3">O ícone escolhido aparece em destaque, num círculo, na página pública.</p>
                <div className="space-y-3">
                  {benefits.map((benefit, index) => {
                    const BenefitIcon = resolveIcon(benefit.icon);
                    return (
                      <div key={benefit._id || index}>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setOpenIconPickerIndex((prev) => (prev === index ? null : index))}
                            title="Escolher ícone"
                            className={`shrink-0 w-10 h-10 rounded-lg border flex items-center justify-center transition-colors ${
                              openIconPickerIndex === index
                                ? 'border-primary-500 bg-primary-50 text-primary-600'
                                : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            <BenefitIcon className="w-5 h-5" />
                          </button>
                          <input
                            type="text"
                            value={benefit.i18n.pt?.text || ''}
                            onChange={(e) => patchBenefit(index, 'pt', e.target.value)}
                            placeholder="Benefício (pt) — traduções en/es via IA acima"
                            className="field flex-1 min-w-0"
                          />
                          <IconButton action="delete" onClick={() => removeBenefit(index)} />
                        </div>
                        {openIconPickerIndex === index && (
                          <div className="mt-2 ml-12">
                            <IconPicker value={benefit.icon} onChange={(iconName) => patchBenefitIcon(index, iconName)} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {benefits.length === 0 && <p className="text-sm text-gray-500">Nenhum benefício adicionado.</p>}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Prova social (números de confiança)</h3>
                    <p className="text-xs text-gray-500 mt-1">Ex.: valor "+500" com legenda "processos concluídos". Aparece antes do CTA.</p>
                  </div>
                  <Button variant="outline" size="sm" className="shrink-0" type="button" onClick={addSocialProofItem}>
                    <Plus className="w-4 h-4" /> Adicionar
                  </Button>
                </div>
                <div className="space-y-3">
                  {socialProof.map((item, index) => (
                    <div key={item._id || index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={item.value}
                        onChange={(e) => patchSocialProofValue(index, e.target.value)}
                        placeholder="+500"
                        className="field w-20 sm:w-24 shrink-0"
                      />
                      <input
                        type="text"
                        value={item.i18n.pt?.label || ''}
                        onChange={(e) => patchSocialProofLabel(index, 'pt', e.target.value)}
                        placeholder="Legenda (pt) — ex.: processos concluídos"
                        className="field flex-1 min-w-0"
                      />
                      <IconButton action="delete" onClick={() => removeSocialProofItem(index)} />
                    </div>
                  ))}
                  {socialProof.length === 0 && <p className="text-sm text-gray-500">Nenhum número de confiança adicionado.</p>}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start justify-between mb-4 gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Depoimentos (até {MAX_TESTIMONIALS})</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Só aparecem depoimentos aprovados que o cliente autorizou para redes sociais e divulgação.
                      A chamada acima deles fica em "Conteúdo" → "Chamada da seção de depoimentos".
                    </p>
                  </div>
                  <span className="text-sm font-medium text-gray-600 whitespace-nowrap">{testimonialIds.length}/{MAX_TESTIMONIALS}</span>
                </div>

                {testimonialIds.length > 0 && (
                  <ol className="space-y-2 mb-4">
                    {testimonialIds.map((tid, index) => {
                      const item = testimonialById.get(tid);
                      const eligible = !!item && isEligibleTestimonial(item);
                      return (
                        <li key={tid} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${eligible ? 'border-primary-200 bg-primary-50' : 'border-amber-300 bg-amber-50'}`}>
                          <span className="text-sm font-semibold text-gray-500 w-5">{index + 1}.</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{item?.author || 'Depoimento removido'}</p>
                            {!eligible && (
                              <p className="text-xs text-amber-700 flex items-center gap-1">
                                <AlertTriangle className="w-3.5 h-3.5" /> Sem autorização de redes sociais — não será exibido e sai ao salvar.
                              </p>
                            )}
                          </div>
                          <IconButton action="moveUp" onClick={() => moveTestimonial(index, -1)} disabled={index === 0} className="disabled:opacity-30" />
                          <IconButton action="moveDown" onClick={() => moveTestimonial(index, 1)} disabled={index === testimonialIds.length - 1} className="disabled:opacity-30" />
                          <IconButton action="remove" onClick={() => toggleTestimonial(tid)} />
                        </li>
                      );
                    })}
                  </ol>
                )}

                {eligibleTestimonials.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Nenhum depoimento aprovado com autorização para redes sociais ainda. Veja em{' '}
                    <button type="button" className="underline" onClick={() => navigate('/admin/testimonials')}>Depoimentos</button>.
                  </p>
                ) : (
                  <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
                    {eligibleTestimonials.map((item) => {
                      const checked = testimonialIds.includes(item._id);
                      const disabled = !checked && testimonialIds.length >= MAX_TESTIMONIALS;
                      return (
                        <label
                          key={item._id}
                          className={`flex items-start gap-3 rounded-lg border p-3 ${checked ? 'border-primary-400 bg-primary-50' : 'border-gray-200'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary-300'}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={() => toggleTestimonial(item._id)}
                            className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-medium text-gray-900">{item.author}</span>
                              {item.country && <span className="text-gray-500">· {item.country}</span>}
                              <span className="inline-flex items-center gap-0.5 text-amber-500">
                                <Star className="w-3.5 h-3.5 fill-current" /> {item.rating || 5}
                              </span>
                              {item.video?.url && <Video className="w-3.5 h-3.5 text-gray-500" aria-label="Com vídeo" />}
                            </div>
                            {item.text && <p className="text-xs text-gray-600 mt-1 line-clamp-2">{item.text}</p>}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Mídia principal (hero)</h3>
                <Tabs
                  variant="segmented"
                  className="mb-4"
                  value={formData.hero.mediaType}
                  onChange={(mediaType) => setFormData((prev) => ({ ...prev, hero: { ...prev.hero, mediaType } }))}
                  items={[
                    { id: 'image', label: 'Imagem' },
                    { id: 'video', label: 'Vídeo' },
                  ]}
                />

                {formData.hero.mediaType === 'image' ? (
                  <>
                    {formData.hero.image.url && (
                      <div className="mb-4">
                        <p className="text-xs text-gray-600 mb-2">
                          Clique no ponto mais importante da imagem (ex.: o rosto). O recorte do hero e das peças para redes sociais é feito em volta dele.
                        </p>
                        <div className="flex justify-center bg-gray-100 rounded-lg p-2">
                          <div className="relative inline-block">
                            <img
                              src={formData.hero.image.url}
                              alt="Imagem selecionada"
                              onClick={handleHeroFocusClick}
                              onLoad={(e) => setHeroImageSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                              className="block max-h-72 w-auto max-w-full rounded cursor-crosshair select-none"
                              draggable={false}
                            />
                            <span
                              aria-hidden="true"
                              className="pointer-events-none absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 border-white bg-primary-600/60 shadow-[0_0_0_2px_rgba(0,0,0,.4)]"
                              style={{ left: `${heroFocusX}%`, top: `${heroFocusY}%` }}
                            />
                          </div>
                        </div>
                        <div className="flex flex-wrap items-end gap-3 mt-3">
                          {HERO_CROP_PREVIEWS.map((crop) => (
                            <div key={crop.label} className="text-center">
                              <div
                                className="rounded-md bg-gray-200 shadow-sm"
                                style={{
                                  width: crop.width,
                                  aspectRatio: `${crop.w} / ${crop.h}`,
                                  backgroundImage: `url("${formData.hero.image.url}")`,
                                  backgroundSize: 'cover',
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: focalObjectPosition(heroFocusX, heroFocusY, heroImageSize?.w || 0, heroImageSize?.h || 0, crop.w, crop.h)
                                }}
                              />
                              <span className="text-[11px] text-gray-500">{crop.label}</span>
                            </div>
                          ))}
                          {(heroFocusX !== 50 || heroFocusY !== 50) && (
                            <button type="button" onClick={() => setHeroFocus(50, 50)} className="link-action text-xs text-primary-700 hover:underline mb-4">
                              Centralizar
                            </button>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-gray-500">Fonte: {HERO_IMAGE_SOURCE_LABELS[formData.hero.image.source] || formData.hero.image.source}</p>
                          <button
                            type="button"
                            onClick={() => setHeroImage({ source: 'upload', url: '' })}
                            className="link-action text-xs text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Remover
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2 sm:gap-3 mb-2">
                      <input ref={heroImageInputRef} type="file" accept={HERO_IMAGE_TYPES.join(',')} onChange={handleHeroImageUpload} className="hidden" />
                      <Button variant="secondary" type="button" onClick={() => heroImageInputRef.current?.click()} disabled={uploadingImage}>
                        <Upload className="w-4 h-4" />
                        <span>{uploadingImage ? 'Enviando...' : 'Enviar imagem'}</span>
                      </Button>
                      <Button variant="secondary" type="button" onClick={handleSearchPixabay} disabled={searchingImages}>
                        <ImageIcon className="w-4 h-4" />
                        <span>{searchingImages ? 'Buscando...' : 'Buscar imagens (Pixabay)'}</span>
                      </Button>
                      <Button variant="secondary" type="button" onClick={() => setShowAiImagePrompt((prev) => !prev)}>
                        <Wand2 className="w-4 h-4" />
                        <span>Gerar imagem com IA</span>
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Recomendado: imagem horizontal, mínimo 1600×900 px (PNG, JPG, WebP ou GIF até 8 MB).</p>
                    <input
                      type="url"
                      value={formData.hero.image.source === 'link' ? formData.hero.image.url : ''}
                      onChange={(e) => setHeroImage({ source: 'link', url: e.target.value })}
                      placeholder="ou cole o link de uma imagem (https://...)"
                      className="field mb-4"
                    />
                    {showAiImagePrompt && (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
                        <input
                          type="text"
                          value={aiPromptInput}
                          onChange={(e) => setAiPromptInput(e.target.value)}
                          placeholder="Descreva a imagem que deseja gerar..."
                          className="field flex-1"
                        />
                        <Button className="whitespace-nowrap" type="button" onClick={handleGenerateAiImage} disabled={generatingAiImage}>
                          {generatingAiImage ? 'Gerando... (pode levar até 1 min)' : 'Gerar'}
                        </Button>
                      </div>
                    )}
                    {pixabayOptions.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {pixabayOptions.map((option) => (
                          <button
                            key={option.pixabayId}
                            type="button"
                            onClick={() => handleSelectPixabayImage(option)}
                            className="rounded-lg overflow-hidden border border-gray-200 hover:ring-2 hover:ring-primary-500 transition-shadow"
                            title={option.tags}
                          >
                            <img src={option.previewURL} alt={option.tags} className="h-20 w-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <Tabs
                      variant="segmented"
                      className="mb-4"
                      value={formData.hero.video.source}
                      onChange={(source) => setFormData((prev) => ({ ...prev, hero: { ...prev.hero, video: { ...prev.hero.video, source } } }))}
                      items={[
                        { id: 'upload', label: 'Upload' },
                        { id: 'link', label: 'Link' },
                      ]}
                    />
                    {formData.hero.video.source === 'upload' ? (
                      <HeroVideoSlots
                        landingPageId={id}
                        value={heroVideos}
                        onChange={setHeroVideos}
                        legacyUrl={formData.hero.video.url && !/^https?:\/\//i.test(formData.hero.video.url) ? formData.hero.video.url : ''}
                      />
                    ) : (
                      <input
                        type="url"
                        value={formData.hero.video.url}
                        onChange={(e) => setFormData((prev) => ({ ...prev, hero: { ...prev.hero, video: { ...prev.hero.video, source: 'link', url: e.target.value } } }))}
                        placeholder="https://www.youtube.com/embed/..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    )}
                    <label className="check-row mt-4 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={formData.hero.video.autoplayMuted !== false}
                        onChange={(e) => setFormData((prev) => ({ ...prev, hero: { ...prev.hero, video: { ...prev.hero.video, autoplayMuted: e.target.checked } } }))}
                        className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>
                        Iniciar automaticamente no mudo
                        <span className="block text-xs text-gray-500">O vídeo toca em loop sem som; o visitante toca em "Ativar som" e ele recomeça do início com áudio.</span>
                      </span>
                    </label>
                  </>
                )}
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-gray-900">Peças para redes sociais</h3>
                  {socialCardsStale && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Desatualizadas — gere de novo
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Gera, a partir da mídia principal, peças prontas com headline, logo, CTA e link: 9:16 (Reels/Stories), 4:5 e 1:1 (feed) e 1.91:1 (preview do link ao compartilhar),
                  além da foto do grupo do WhatsApp (640×640, pensada para o recorte redondo, com o texto curto definido no conteúdo).
                  A 1.91:1 passa a ser usada automaticamente no preview do link quando não houver imagem OG própria.
                </p>

                {!isEditing ? (
                  <p className="text-sm text-gray-500">Salve a landing page primeiro para gerar as peças.</p>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span className="text-sm text-gray-700">Idiomas:</span>
                        {CARD_LANGS.map((lang) => (
                          <label key={lang} className="check-row items-center gap-2 text-sm text-gray-700">
                            <input type="checkbox" checked={cardLangs.includes(lang)} onChange={() => toggleCardLang(lang)} />
                            {lang.toUpperCase()}
                          </label>
                        ))}
                      </div>
                      <Button variant="secondary"
                        type="button"
                        onClick={handleGenerateSocialCards}
                        disabled={generatingCards || cardLangs.length === 0}>
                        <Wand2 className="w-4 h-4" />
                        <span>{generatingCards ? 'Gerando peças...' : socialCards.length ? 'Gerar de novo' : 'Gerar peças'}</span>
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">Salve as alterações de texto/imagem antes de gerar — as peças usam o que está salvo.</p>

                    {socialCards.length > 0 && (
                      <>
                        {generatedLangs.length > 1 && (
                          <Tabs
                            variant="segmented"
                            className="mb-4"
                            value={previewLang}
                            onChange={setPreviewLang}
                            items={generatedLangs.map((lang) => ({ id: lang, label: lang.toUpperCase() }))}
                          />
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 items-start">
                          {SOCIAL_CARD_FORMATS.map(({ format, label, hint, aspect, postable }) => {
                            const card = cardsForPreview.find((c) => c.format === format);
                            if (!card) return null;
                            return (
                              <div key={format} className="flex flex-col gap-2">
                                {/* Foto do grupo: prévia redonda, como o WhatsApp mostra (o arquivo baixado é quadrado). */}
                                <div className={`${aspect} w-full overflow-hidden ${format === 'whatsapp' ? 'rounded-full' : 'rounded-lg'} border border-gray-200 bg-gray-100`}>
                                  <img src={card.url} alt={`Peça ${label}`} className="h-full w-full object-cover" loading="lazy" />
                                </div>
                                <div className="text-sm">
                                  <span className="font-medium text-gray-900">{label}</span>
                                  <span className="text-gray-500"> · {hint}</span>
                                  <span className="block text-xs text-gray-400">{card.width}×{card.height}</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <Button variant="secondary" size="sm" href={card.url} download>
                                    <Download className="w-3.5 h-3.5" />
                                    Baixar
                                  </Button>
                                  {postable && (
                                    <Button variant="secondary" size="sm"
                                      type="button"
                                      onClick={() => handleCreateSocialPost(format)}
                                      disabled={creatingPostFormat !== null}>
                                      <Share2 className="w-3.5 h-3.5" />
                                      {creatingPostFormat === format ? 'Criando...' : 'Criar post'}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-xs text-gray-500 mt-4">
                          O 9:16 vai para Stories/Reels pelo app (use como capa ou com áudio) — a publicação automática do Instagram só aceita feed (4:5 a 1.91:1).
                          &quot;Criar post&quot; abre um rascunho em Redes Sociais com a peça, a legenda e o link com UTM.
                          A foto do grupo é baixada e trocada à mão no WhatsApp (Dados do grupo → foto) — não há API para isso.
                        </p>
                      </>
                    )}
                  </>
                )}
              </div>

              <LandingQuizEditor value={quiz} onChange={setQuiz} />

              <div className="form-actions pt-2">
                <Button variant="secondary" type="button" onClick={() => navigate('/admin/landing-pages')}>
                  {t('common.cancel')}
                </Button>
                <Button variant="outline" type="button" disabled={loading} onClick={(e) => handleSubmit(e as any, false)}>
                  Salvar rascunho
                </Button>
                <Button type="button" disabled={loading} onClick={(e) => handleSubmit(e as any, true)}>
                  <Save className="w-4 h-4" />
                  <span>{loading ? t('common.saving') : 'Salvar e publicar'}</span>
                </Button>
              </div>
            </form>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Avatar</h3>
              <select
                value={formData.avatar}
                onChange={(e) => setFormData((prev) => ({ ...prev, avatar: e.target.value }))}
                className="field field--select field--lg"
              >
                <option value="">Selecione um avatar</option>
                {avatars.map((avatar) => (
                  <option key={avatar._id} value={avatar._id}>{avatar.name}</option>
                ))}
              </select>
              {avatars.length === 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  Nenhum avatar cadastrado ainda. Crie um em <button type="button" className="underline" onClick={() => navigate('/admin/avatars/new')}>Avatares</button>.
                </p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Destino do botão (CTA)</h3>
              <CtaTargetPicker
                value={formData.ctaTarget}
                onChange={(ctaTarget) => setFormData((prev) => ({ ...prev, ctaTarget }))}
                ownSlug={formData.slug}
                avatarSlug={avatars.find((a) => a._id === formData.avatar)?.slug || ''}
                quizEnabled={quiz.enabled}
              />
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Valor e garantias</h3>
              <PriceDisplayPicker
                value={formData.priceDisplay}
                onChange={(priceDisplay) => setFormData((prev) => ({ ...prev, priceDisplay }))}
                suggestedSlug={formData.ctaTarget.kind === 'community' ? formData.ctaTarget.value : (quiz.communitySlug || '')}
              />
              <div className="mt-4 border-t pt-4">
                <Checkbox
                  checked={formData.paymentTrust.enabled}
                  onChange={(e) => setFormData((prev) => ({ ...prev, paymentTrust: { enabled: e.target.checked } }))}
                  label="Mostrar pagamento seguro (Stripe) e garantia de 7 dias"
                  description="Selos abaixo do botão, no topo e no fim da página. Mesmo texto da página de pagamento da comunidade."
                />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Consultor(a) exibido(a) na página (opcional)</h3>
              <select
                value={formData.teamMember}
                onChange={(e) => setFormData((prev) => ({ ...prev, teamMember: e.target.value }))}
                className="field field--select field--lg"
              >
                <option value="">Nenhum</option>
                {teamMembers.map((member) => (
                  <option key={member._id} value={member._id}>
                    {member.nome}{member.cargo ? ` — ${member.cargo}` : ''}
                  </option>
                ))}
              </select>
              {teamMembers.length === 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  Nenhum membro de equipe cadastrado ainda. Crie um em{' '}
                  <button type="button" className="underline" onClick={() => navigate('/admin/equipe')}>Equipe</button>.
                </p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Status</h3>
              <select
                value={formData.status}
                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as 'draft' | 'published' }))}
                className="field field--select field--lg"
              >
                <option value="draft">Rascunho</option>
                <option value="published">Publicada</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPageForm;
